import React, { useState, useEffect, useCallback } from 'react';
import axios from './api/axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  Snackbar,
  Alert,
  Fade,
  Slide
} from '@mui/material';
import {
  Add,
  Translate,
  ArrowBack,
  Language as LanguageIcon
} from '@mui/icons-material';
import GlossaryTermDialog from './components/glossary/GlossaryTermDialog';
import GlossaryTranslationDialog from './components/glossary/GlossaryTranslationDialog';
import GlossaryTermForm from './components/glossary/GlossaryTermForm';
import LanguageManager from './LanguageManager';
import GlossaryCard from './components/glossary/GlossaryCard';
import GlossaryListSkeleton from './components/glossary/GlossaryListSkeleton';
import TermsTable from './components/glossary/TermsTable';

const GlossaryManager = ({ open = false, onClose, userPermissions, embedded = false }) => {
  // Main state
  const [view, setView] = useState('list'); // 'list' or 'detail'
  const [glossaries, setGlossaries] = useState([]);
  const [selectedGlossary, setSelectedGlossary] = useState(null);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  
  // Reference data
  const [availableLanguages, setAvailableLanguages] = useState([]);
  
  // UI state
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form dialogs
  const [showGlossaryForm, setShowGlossaryForm] = useState(false);
  const [showTermDialog, setShowTermDialog] = useState(false);
  const [showTermForm, setShowTermForm] = useState(false);
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [showLanguageManager, setShowLanguageManager] = useState(false);
  const [editingGlossary, setEditingGlossary] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  const [editingTranslation, setEditingTranslation] = useState(null);
  
  // Inline editing state
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingRowData, setEditingRowData] = useState({
    source_text: '',
    translated_text: ''
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const [deleteGlossaryConfirmOpen, setDeleteGlossaryConfirmOpen] = useState(false);
  const [glossaryToDelete, setGlossaryToDelete] = useState(null);
  
  // Glossary form data
  const [glossaryFormData, setGlossaryFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    if (embedded || open) {
      // Cargar en paralelo para reducir tiempo de espera
      Promise.all([loadGlossaries(), loadLanguages()]);
    }
  }, [embedded, open]);

  useEffect(() => {
    if (selectedGlossary) {
      loadGlossaryTerms(selectedGlossary.id);
    }
  }, [selectedGlossary]);

  // Auto-select first active language
  useEffect(() => {
    if (availableLanguages.length > 0 && !selectedLanguage) {
      const defaultLang = availableLanguages.find(l => l.code === 'en') || availableLanguages[0];
      setSelectedLanguage(defaultLang);
    }
  }, [availableLanguages, selectedLanguage]);

  // Snackbar helper
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // API calls
  const loadGlossaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/terminology/glossaries/');
      setGlossaries(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading glossaries:', error);
      showSnackbar('Error loading glossaries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadGlossaryTerms = async (glossaryId) => {
    setLoading(true);
    try {
      const response = await axios.get(`terminology/glossaries/${glossaryId}/terms/`);
      setGlossaryTerms(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading glossary terms:', error);
      showSnackbar('Error loading glossary terms', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadLanguages = async () => {
    try {
      const response = await axios.get('/terminology/languages/');
      const languages = response.data.results || response.data || [];
      setAvailableLanguages(languages.filter(l => l.is_active));
    } catch (error) {
      console.error('Error loading languages:', error);
      showSnackbar('Error loading languages', 'error');
    }
  };

  // Glossary CRUD
  const handleCreateGlossary = async () => {
    try {
      const response = await axios.post('/terminology/glossaries/', glossaryFormData);
      showSnackbar('Glossary created successfully');
      setShowGlossaryForm(false);
      resetGlossaryForm();
      loadGlossaries();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating glossary', 'error');
    }
  };

  const handleUpdateGlossary = async () => {
    try {
      await axios.put(`terminology/glossaries/${editingGlossary.id}/`, glossaryFormData);
      showSnackbar('Glossary updated successfully');
      setShowGlossaryForm(false);
      setEditingGlossary(null);
      resetGlossaryForm();
      loadGlossaries();
      if (selectedGlossary && selectedGlossary.id === editingGlossary.id) {
        setSelectedGlossary({ ...selectedGlossary, ...glossaryFormData });
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error updating glossary', 'error');
    }
  };

  const handleDeleteGlossaryClick = (glossary) => {
    setGlossaryToDelete(glossary);
    setDeleteGlossaryConfirmOpen(true);
  };

  const handleConfirmDeleteGlossary = async () => {
    if (!glossaryToDelete) return;

    try {
      await axios.delete(`terminology/glossaries/${glossaryToDelete.id}/`);
      showSnackbar('Glossary deleted successfully');
      setDeleteGlossaryConfirmOpen(false);
      setGlossaryToDelete(null);
      loadGlossaries();
      if (selectedGlossary && selectedGlossary.id === glossaryToDelete.id) {
        setSelectedGlossary(null);
        setView('list');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting glossary', 'error');
    }
  };

  const handleCancelDeleteGlossary = () => {
    setDeleteGlossaryConfirmOpen(false);
    setGlossaryToDelete(null);
  };

  const handleEditGlossary = (glossary) => {
    setEditingGlossary(glossary);
    setGlossaryFormData({
      name: glossary.name,
      description: glossary.description || '',
      is_active: glossary.is_active
    });
    setShowGlossaryForm(true);
  };

  const resetGlossaryForm = () => {
    setGlossaryFormData({
      name: '',
      description: '',
      is_active: true
    });
    setEditingGlossary(null);
  };

  // Term CRUD
  const handleTermSaved = () => {
    setShowTermDialog(false);
    setEditingTerm(null);
    if (selectedGlossary) {
      loadGlossaryTerms(selectedGlossary.id);
    }
    showSnackbar('Term saved successfully');
  };

  const handleEditTerm = (term) => {
    setEditingTerm(term);
    setShowTermDialog(true);
  };

  const handleDeleteTermClick = (term) => {
    setTermToDelete(term);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteTerm = async () => {
    if (!termToDelete) return;

    try {
      await axios.delete(`terminology/glossary-terms/${termToDelete.id}/`);
      showSnackbar('Term deleted successfully');
      setDeleteConfirmOpen(false);
      setTermToDelete(null);
      if (selectedGlossary) {
        loadGlossaryTerms(selectedGlossary.id);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting term', 'error');
    }
  };

  const handleCancelDeleteTerm = () => {
    setDeleteConfirmOpen(false);
    setTermToDelete(null);
  };

  // Translation handlers
  const handleAddTranslation = (term) => {
    setEditingTerm(term);
    setEditingTranslation(null);
    setShowTranslationDialog(true);
  };

  const handleEditTranslation = (term, translation) => {
    setEditingTerm(term);
    setEditingTranslation(translation);
    setShowTranslationDialog(true);
  };

  const handleTranslationSaved = () => {
    setShowTranslationDialog(false);
    setEditingTerm(null);
    setEditingTranslation(null);
    if (selectedGlossary) {
      loadGlossaryTerms(selectedGlossary.id);
    }
    showSnackbar('Translation saved successfully');
  };

  // Inline editing handlers
  const handleStartRowEdit = (term, translation) => {
    setEditingRowId(term.id);
    setEditingRowData({
      source_text: term.source_text || '',
      translated_text: translation?.translated_text || ''
    });
  };

  const handleCancelRowEdit = () => {
    setEditingRowId(null);
    setEditingRowData({ source_text: '', translated_text: '' });
  };

  const handleSaveRowEdit = async (term, translation) => {
    if (!editingRowData.source_text || !editingRowData.source_text.trim()) {
      showSnackbar('Source text cannot be empty', 'error');
      return;
    }

    try {
      // Update term source_text
      await axios.patch(`terminology/glossary-terms/${term.id}/`, {
        source_text: editingRowData.source_text.trim()
      });

      // Update translation if exists
      if (translation && editingRowData.translated_text && editingRowData.translated_text.trim()) {
        await axios.patch(`terminology/glossary-term-translations/${translation.id}/`, {
          translated_text: editingRowData.translated_text.trim()
        });
      }

      showSnackbar('Updated successfully');
      setEditingRowId(null);
      setEditingRowData({ source_text: '', translated_text: '' });
      if (selectedGlossary) {
        loadGlossaryTerms(selectedGlossary.id);
      }
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar(error.response?.data?.message || error.response?.data?.detail || 'Error saving changes', 'error');
    }
  };

  // View handlers
  const handleViewGlossaryDetails = (glossary) => {
    setSelectedGlossary(glossary);
    setView('detail');
  };

  const handleBackToList = () => {
    setSelectedGlossary(null);
    setView('list');
  };

  // Helper functions
  const getTermTranslation = (term, languageCode) => {
    if (!term.translations) return null;
    return term.translations.find(t => t.language === languageCode || t.language?.code === languageCode);
  };

  const content = (
    <>
      <Box
        sx={{
          p: embedded ? { xs: 1.5, sm: 2, lg: 3 } : 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Box display="flex" alignItems="center" gap={2} minWidth={0}>
            {view === 'detail' && (
              <Tooltip title="Back to list">
                <IconButton
                  onClick={handleBackToList}
                  size="small"
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
            )}
            <Translate sx={{ fontSize: 28, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {view === 'list' ? 'Glossary Management' : selectedGlossary?.name}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {view === 'list' && userPermissions?.can_create && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setShowGlossaryForm(true)}
                sx={{ fontWeight: 500 }}
              >
                New Glossary
              </Button>
            )}
            <Tooltip title="Manage Languages">
              <Button
                variant="outlined"
                size="small"
                startIcon={<LanguageIcon />}
                onClick={() => setShowLanguageManager(true)}
                sx={{ fontWeight: 500 }}
              >
                Languages
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          height: 'auto',
          p: { xs: 1, sm: 1.5, lg: 2 },
          display: 'flex',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '100%',
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Fade in={view === 'list'} timeout={300}>
            <Box
              sx={{
                display: view === 'list' ? 'flex' : 'none',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                minHeight: 0,
              }}
            >
              {/* List View */}
            {loading ? (
              <GlossaryListSkeleton count={6} />
            ) : glossaries.length === 0 ? (
              <Paper 
                variant="outlined" 
                sx={{ 
                  py: 10, 
                  textAlign: 'center',
                  bgcolor: 'grey.50'
                }}
              >
                <Translate sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No glossaries created yet
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  Create your first glossary to start managing translations
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setShowGlossaryForm(true)}
                >
                  Create Glossary
                </Button>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  width: '100%',
                  gap: { xs: 1.5, lg: 3 },
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))'
                  },
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pr: 0.5,
                  pb: 0.5,
                }}
              >
                {glossaries.map((glossary) => (
                  <GlossaryCard
                    key={glossary.id}
                    glossary={glossary}
                    onManage={() => handleViewGlossaryDetails(glossary)}
                    onEdit={userPermissions?.can_edit ? () => handleEditGlossary(glossary) : null}
                    onDelete={userPermissions?.can_delete ? () => handleDeleteGlossaryClick(glossary) : null}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Fade>

        <Fade in={view === 'detail'} timeout={300}>
          <Box display={view === 'detail' ? 'block' : 'none'}>
            {/* Detail View */}
            {selectedGlossary && (
              <>
                <Paper
                  sx={{ 
                    p: { xs: 1.5, sm: 2 },
                    mb: { xs: 1.5, sm: 2 },
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {selectedGlossary.description || (
                          <Box component="span" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                            No description provided
                          </Box>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Status
                      </Typography>
                      <Box mt={0.5}>
                        {selectedGlossary.is_active ? (
                          <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                            ● Active
                          </Typography>
                        ) : (
                          <Typography variant="body1" color="text.disabled" sx={{ fontWeight: 600 }}>
                            ● Inactive
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Total Terms
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {glossaryTerms.length}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {userPermissions?.can_create && (
                  <Box display="flex" justifyContent="flex-end" mb={{ xs: 1.5, sm: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setShowTermForm(true)}
                      size="large"
                      sx={{ fontWeight: 500 }}
                    >
                      Add Term
                    </Button>
                  </Box>
                )}

                <TermsTable
                  terms={glossaryTerms}
                  languages={availableLanguages}
                  onEditTerm={userPermissions?.can_edit ? handleEditTerm : null}
                  onDeleteTerm={userPermissions?.can_delete ? handleDeleteTermClick : null}
                  loading={loading}
                />
              </>
            )}
          </Box>
        </Fade>
        </Box>
      </Box>

      {!embedded && (
        <>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 500 }}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </>
  );

  return (
    <>
      {embedded ? (
        <Paper elevation={2} sx={{ height: '100%', minHeight: 0, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {content}
        </Paper>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="xl"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
        >
          {content}
        </Dialog>
      )}

      {/* Glossary Form Dialog */}
      <Dialog 
        open={showGlossaryForm} 
        onClose={() => { setShowGlossaryForm(false); resetGlossaryForm(); }} 
        maxWidth="sm" 
        fullWidth
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingGlossary ? 'Edit Glossary' : 'New Glossary'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label="Name"
              value={glossaryFormData.name}
              onChange={(e) => setGlossaryFormData({ ...glossaryFormData, name: e.target.value })}
              required
              autoFocus
              variant="outlined"
            />
            
            <TextField
              fullWidth
              label="Description"
              value={glossaryFormData.description}
              onChange={(e) => setGlossaryFormData({ ...glossaryFormData, description: e.target.value })}
              multiline
              rows={4}
              variant="outlined"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={glossaryFormData.is_active}
                  onChange={(e) => setGlossaryFormData({ ...glossaryFormData, is_active: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Typography variant="body1">
                  Active
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => { setShowGlossaryForm(false); resetGlossaryForm(); }}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={editingGlossary ? handleUpdateGlossary : handleCreateGlossary}
            disabled={!glossaryFormData.name}
            sx={{ fontWeight: 500 }}
          >
            {editingGlossary ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Term Dialog */}
      {showTermDialog && (
        <GlossaryTermDialog
          open={showTermDialog}
          onClose={() => { setShowTermDialog(false); setEditingTerm(null); }}
          onSaved={handleTermSaved}
          glossaryId={selectedGlossary?.id}
          term={editingTerm}
          languages={availableLanguages}
        />
      )}

      {/* Term Form (New Unified Form) */}
      {showTermForm && (
        <GlossaryTermForm
          open={showTermForm}
          onClose={() => setShowTermForm(false)}
          onSaved={() => {
            setShowTermForm(false);
            if (selectedGlossary) {
              loadGlossaryTerms(selectedGlossary.id);
            }
          }}
          glossaryId={selectedGlossary?.id}
        />
      )}

      {/* Translation Dialog */}
      {showTranslationDialog && (
        <GlossaryTranslationDialog
          open={showTranslationDialog}
          onClose={() => { setShowTranslationDialog(false); setEditingTerm(null); setEditingTranslation(null); }}
          onSaved={handleTranslationSaved}
          term={editingTerm}
          translation={editingTranslation}
          languages={availableLanguages}
        />
      )}

      {/* Delete Term Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDeleteTerm}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
            Delete Term
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this term?
          </Typography>
          {termToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1, border: '1px solid', borderColor: 'error.light' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Source Text:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, mb: 1.5 }}>
                {termToDelete.source_text}
              </Typography>
              <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                ⚠ This will delete the term and all its translations.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={handleCancelDeleteTerm}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteTerm} 
            color="error" 
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Glossary Confirmation Dialog */}
      <Dialog
        open={deleteGlossaryConfirmOpen}
        onClose={handleCancelDeleteGlossary}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
            Delete Glossary
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this glossary?
          </Typography>
          {glossaryToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1, border: '1px solid', borderColor: 'error.light' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Glossary Name:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, mb: 1.5 }}>
                {glossaryToDelete.name}
              </Typography>
              <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                ⚠ This will delete the glossary and all its terms with translations.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={handleCancelDeleteGlossary}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteGlossary} 
            color="error" 
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Language Manager Dialog */}
      <Dialog 
        open={showLanguageManager} 
        onClose={() => {
          setShowLanguageManager(false);
          loadLanguages(); // Reload languages when closing
        }}
        maxWidth="lg"
        fullWidth
      >
        <LanguageManager token={localStorage.getItem('token')} />
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GlossaryManager;
